import React, { FC, useContext, useState } from "react";
import { observer } from "mobx-react-lite";
import { Context } from "../../../index";
import { toast } from "react-toastify";
import SVG from "../../svg/Svg";
import CustomToastContainer from "../notification/CustomToastContainer";
import ConfirmToast from "../notification/Confirm";
import {
  IModules,
  ILines,
  IStation,
  IDeviceSecretIssued,
  IDeviceSecretRevoked,
  IDeviceSecretStatus,
} from "../../../models/IUser";
import { PopupModuleHeader } from "./PopupModuleHeader";
import { PopupModuleInfo, getFieldLabel } from "./PopupModuleInfo";
import { PopupStationsTable } from "./PopupStationsTable";
import { PopupMeterReplace } from "./PopupMeterReplace";
import { PopupChildModulesTable } from "./PopupChildModulesTable";
import { PopupModuleActions } from "./PopupModuleActions";
import { PopupModuleDeviceSecret } from "./PopupModuleDeviceSecret";
import { PopupModuleApPassword } from "./PopupModuleApPassword";
import { PopupModuleStationDiag } from "./PopupModuleStationDiag";
import { CabinetModelSwitch } from "./CabinetModelSwitch";
import { useModuleForm } from "./hooks/useModuleForm";
import { usePollingConfirmation } from "./hooks/usePollingConfirmation";
import { visibleStations } from "./hooks/useStationsState";
import { useModuleUpdate } from "./hooks/useModuleUpdate";
import { useModuleRestart } from "./hooks/useModuleRestart";
import { useModuleDeviceSecret } from "./hooks/useModuleDeviceSecret";
import { placeCellError } from "../../../utils/placeCellError";
import { meterCellError } from "../../../utils/meterCellError";
import { logApiError } from "../../../utils/logApiError";
import apiService from "../../../services/apiService";
import { pollingErrorMessage } from "../../../utils/pollingError";
import "../popstyle_module.css";

interface PopupModuleProps {
  module: IModules | null;
  lines: ILines[];
  stations: IStation[];
  modules: IModules[];
  handleCloseModal: () => void;
  updateModule?: (
    module: Partial<IModules>,
    stations: Partial<IStation>[],
    childModules: Partial<IModules>[],
    modifiedKeys: string[],
  ) => Promise<any>;
  updateChildModules?: (
    parentModule: Partial<IModules>,
    childModules: Partial<IModules>[],
    modifiedKeys: string[],
  ) => Promise<any>;
  updateSmbModules?: (
    module: Partial<IModules>,
    stations: Partial<IStation>[],
    modifiedKeys: string[],
  ) => Promise<any>;
  deleteModule?: (id: number) => Promise<void>;
  restartModule?: (identifier: string) => Promise<any>;
  // DEV-78 (ПРВ-06, ПРВ-05): секрет изделия — только для root, значение
  // приходит один раз в ответе выдачи.
  fetchDeviceSecretStatus?: (moduleId: number) => Promise<IDeviceSecretStatus>;
  issueDeviceSecret?: (moduleId: number) => Promise<IDeviceSecretIssued>;
  revokeDeviceSecret?: (moduleId: number) => Promise<IDeviceSecretRevoked>;
  // `Б2.2` / `Б4`: пароль точки — чтение по нажатию, смена командой, внесение
  // из паспорта. Всё только root; значение в ответах списков не приходит.
  fetchApPassword?: (
    moduleId: number,
  ) => Promise<{ apPassword: string | null; state: string; changedAt: string | null }>;
  rotateApPassword?: (
    moduleId: number,
  ) => Promise<{ state: string; changedAt: string | null }>;
  importApPassword?: (
    moduleId: number,
    apPassword: string,
  ) => Promise<{ state: string; changedAt: string | null }>;
  refreshData?: () => void;
}

const PopupModule: FC<PopupModuleProps> = ({
  module,
  lines,
  stations,
  modules,
  handleCloseModal,
  updateModule,
  updateChildModules,
  updateSmbModules,
  deleteModule,
  restartModule: restartModuleProp,
  fetchDeviceSecretStatus,
  issueDeviceSecret,
  revokeDeviceSecret,
  fetchApPassword,
  rotateApPassword,
  importApPassword,
  refreshData,
}) => {
  const { store } = useContext(Context);

  // `DEV-93`: станция, для которой открыта форма замены счётчика.
  const [meterReplaceStation, setMeterReplaceStation] =
    useState<IStation | null>(null);
  // `R.7`: пока окно открыто — «Применяется…», затем итог по `polling_applied`.
  const { pollingOverrides, markPollingPending } = usePollingConfirmation();

  /**
   * `S.2`: переключатель опроса станции. Выключение — с подтверждением:
   * станция перестанет опрашиваться и ток ей распределяться не будет.
   */
  const togglePolling = async (station: IStation) => {
    const enabled = station.polling_enabled === false;
    const number = station.controller_address ?? station.id;

    if (!enabled) {
      const confirmed = window.confirm(
        `Станция ${number} перестанет опрашиваться, ток ей распределяться не будет`,
      );
      if (!confirmed) return;
    }

    try {
      await apiService.PATCH(`/station/${station.id}/polling`, { enabled });
      // `R.7`: сразу «Применяется…», затем перечитываем строку.
      markPollingPending(station.id, enabled);
      if (refreshData) refreshData();
    } catch (error: any) {
      toast.error(pollingErrorMessage(error, number));
    }
  };

  const limit = module
    ? lines.find((line) => line.id === module.line_id)?.current_limit_line || ""
    : "";

  const {
    editedModule,
    isMutated,
    config,
    changedModuleFields,
    fieldErrors,
    validateModuleFields,
    stationsState,
    childModulesState,
    handleModuleChange,
    handleStationChange,
    handleChildModuleChange,
    setModuleField,
    applyPlaceError,
    applyMeterError,
    addChildSlot,
    resetAfterSave,
    changedModuleFieldKeys,
    modifiedStations,
    modifiedChildModules,
  } = useModuleForm(module, stations, modules);

  const { handleResponse, handleError } = useModuleUpdate();
  const { restart } = useModuleRestart(restartModuleProp, refreshData);

  // DEV-78: секрет изделия — только root. Хук вызывается всегда (правило хуков),
  // но при `enabled: false` ни читает признак, ни зовёт операции.
  const isRoot = store.user.role === "root";
  const {
    status: deviceSecretStatus,
    issuedSecret,
    isLoading: isDeviceSecretLoading,
    handleIssue,
    handleRevoke,
    hideIssuedSecret,
  } = useModuleDeviceSecret({
    moduleId: module?.id,
    enabled: isRoot,
    // `п. 1`: перевыпуск подтверждается вводом идентификатора изделия.
    chargeId: module?.charge_id,
    fetchStatus: fetchDeviceSecretStatus,
    issue: issueDeviceSecret,
    revoke: revokeDeviceSecret,
  });

  async function sendUpdate(params: {
    apiFn?: (...args: unknown[]) => Promise<any>;
    apiName: string;
    withChargeId?: boolean;
    extraArgs: unknown[];
  }): Promise<boolean> {
    try {
      if (!params.apiFn) {
        toast.error(`Функция ${params.apiName} не передана`);
        return false;
      }
      if (!editedModule) return false;

      const changedFields = changedModuleFieldKeys.reduce(
        (acc, key) => {
          if (key in editedModule) {
            acc[key] = editedModule[key as keyof IModules];
          }
          return acc;
        },
        {} as Partial<Record<keyof IModules, unknown>>,
      );

      const module: Partial<IModules> = {
        id: editedModule.id,
        ...(params.withChargeId !== false && {
          charge_id: editedModule.charge_id,
        }),
        ...changedFields,
      };

      const response = await params.apiFn(module, ...params.extraArgs);
      return handleResponse(response);
    } catch (error) {
      // `31.1` (`РГ-07`): отказ по месту — у самой ячейки, остальное — как было.
      const placeProblem = placeCellError(error);
      if (placeProblem) applyPlaceError(placeProblem);

      // `DEV-91.4`: отказ по счётчику — у строки той станции, которую назвал
      // back; станция берётся из текста ответа, а не «всем правленым строкам».
      const meterProblem = meterCellError(error);
      if (meterProblem) {
        applyMeterError(meterProblem.message, meterProblem.station);
      }

      handleError(error);
      return false;
    }
  }

  async function sendDeleteModule(id: number) {
    try {
      const toastId = `delete-module-${id}`;
      toast(
        <ConfirmToast
          message={`Удалить модуль - ${module?.charge_id} ?`}
          onConfirm={async () => {
            if (deleteModule) {
              await deleteModule(id);
            } else {
              toast.error("Функция deleteModule не передана");
            }
            if (refreshData) refreshData();
            handleCloseModal();
          }}
          type="del"
          title="удалить"
          toastId={toastId}
        />,
        {
          toastId,
          style: {
            borderRadius: 0,
          },
          progressClassName: "toast-progress-delete",
          autoClose: 5000,
        },
      );
    } catch (error) {
      logApiError("Ошибка при удалении модуля:", error);
    }
  }

  const saveChanges = async () => {
    const validationErrors = validateModuleFields();
    if (Object.keys(validationErrors).length > 0) {
      const problems = Object.entries(validationErrors)
        .map(([key, msg]) => `${getFieldLabel(key)} — ${msg}`)
        .join("; ");
      toast.error(`Исправьте ошибки: ${problems}`);
      return;
    }

    let success = false;

    switch (config.saveMode) {
      case "smb":
        success = await sendUpdate({
          apiFn: updateSmbModules,
          apiName: "updateSmbModules",
          extraArgs: [modifiedStations, changedModuleFieldKeys],
        });
        break;
      case "childModules":
        success = await sendUpdate(
          updateChildModules
            ? {
                apiFn: updateChildModules,
                apiName: "updateChildModules",
                withChargeId: false,
                extraArgs: [modifiedChildModules, changedModuleFieldKeys],
              }
            : {
                apiFn: updateModule,
                apiName: "updateModule",
                extraArgs: [
                  modifiedStations,
                  modifiedChildModules,
                  changedModuleFieldKeys,
                ],
              },
        );
        break;
      default:
        success = await sendUpdate({
          apiFn: updateModule,
          apiName: "updateModule",
          extraArgs: [
            modifiedStations,
            modifiedChildModules,
            changedModuleFieldKeys,
          ],
        });
    }

    if (success) {
      resetAfterSave();
    }
  };

  if (!module?.id) {
    return null;
  }

  // `К2.6.1`: строки показываем по модели из формы, а не по сохранённой, —
  // иначе переключение 9/15 не видно до «Сохранить».
  const cabinetModel = (editedModule || module).cabinet_model;
  // `R.7`: состояние переключения опроса накладывается на строку, пока окно
  // открыто: «Применяется…» → «вкл/выкл» по `polling_applied`, через 30 с —
  // «не подтвердило», переключатель снова доступен.
  const stationsToShow = visibleStations(stationsState, cabinetModel).map(
    (row) => {
      const override = pollingOverrides[row.id];
      if (!override) return row;

      const state =
        override.state === "pending" || override.state === "timeout"
          ? override.state
          : undefined;

      return {
        ...row,
        polling_state: state,
        polling_applied: override.state === "applied",
        polling_enabled: override.enabled,
      } as typeof row;
    },
  );

  return (
    <>
      <div className="popup_container">
        <PopupModuleHeader
          module={module}
          onRestart={restart}
          onClose={handleCloseModal}
        />

        <PopupModuleInfo
          module={module}
          editedModule={editedModule}
          modules={modules}
          lines={lines}
          limit={limit}
          changedModuleFields={changedModuleFields}
          fieldErrors={fieldErrors}
          onChange={handleModuleChange}
          onLineChange={(lineId) => setModuleField("line_id", lineId)}
          config={config}
        />

        <div className="popup_module_stations">
          <div>
            <span>
              {config.tableMode === "childModules"
                ? "ДОЧЕРНИЕ МОДУЛИ"
                : "СТАНЦИИ МОДУЛЯ"}
            </span>
            <span style={{ marginLeft: "8px" }}>{SVG["broken_line"]}</span>
          </div>
          {/* `К2.6`: модель шкафа — у МДБН и СМБ, в строке станций; у ММБ
              (дочерние модули) её нет. Значение уходит общей кнопкой «Сохранить». */}
          {config.tableMode === "stations" ? (
            <CabinetModelSwitch
              isRoot={isRoot}
              deviceType={module.device_type}
              value={(editedModule || module).cabinet_model}
              onChange={(model) => setModuleField("cabinet_model", model)}
            />
          ) : null}
        </div>

        {config.tableMode === "childModules" ? (
          <PopupChildModulesTable
            data={childModulesState}
            onCellChange={handleChildModuleChange}
            onAdd={addChildSlot}
          />
        ) : (
          <PopupStationsTable
            data={stationsToShow}
            onCellChange={handleStationChange}
            onMeterReplace={setMeterReplaceStation}
            onPollingToggle={togglePolling}
          />
        )}

        <PopupModuleStationDiag moduleId={module.id} />

        <PopupModuleDeviceSecret
          isRoot={isRoot}
          status={deviceSecretStatus}
          isLoading={isDeviceSecretLoading}
          issuedSecret={issuedSecret}
          onIssue={handleIssue}
          onRevoke={handleRevoke}
          onHideIssued={hideIssuedSecret}
        />

        <PopupModuleApPassword
          isRoot={isRoot}
          onFetch={async () => {
            if (!fetchApPassword) throw new Error("чтение пароля недоступно");

            return fetchApPassword(module.id);
          }}
          onRotate={async () => {
            if (!rotateApPassword) throw new Error("смена пароля недоступна");

            const result = await rotateApPassword(module.id);

            if (result.state === "pending") {
              toast.info("пароль отправлен изделию и ждёт подтверждения");
            }
            if (result.state === "confirmed") {
              toast.success("пароль точки подтверждён изделием");
            }

            return result;
          }}
          onImport={async (apPassword) => {
            if (!importApPassword) throw new Error("внесение пароля недоступно");

            const result = await importApPassword(module.id, apPassword);
            toast.success("пароль точки внесён из паспорта");

            return result;
          }}
        />

        <PopupModuleActions
          isMutated={isMutated}
          canDelete={store.user.role === "root"}
          onSave={saveChanges}
          onDelete={() => sendDeleteModule(module.id)}
        />
      </div>

      {/* `DEV-93`: форма замены счётчика у выбранной станции. */}
      {meterReplaceStation ? (
        <PopupMeterReplace
          station={meterReplaceStation}
          onClose={() => setMeterReplaceStation(null)}
          onSaved={() => {
            setMeterReplaceStation(null);
            toast.success("счётчик заменён");
            if (refreshData) refreshData();
          }}
        />
      ) : null}

      <CustomToastContainer />
    </>
  );
};

export default observer(PopupModule);
